import {
  useState, useMemo 
} from 'react';
import {
  ShoppingCart, Gavel, Clock, DollarSign, AlertTriangle 
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CrudToolbar } from '@/components/ui/crud-toolbar';
import {
  useSourcingEvents,
  SourcingEventCard,
  SourcingEventDetailModal,
  AddEventModal,
  type AddEventFormData,
  type SourcingStatus,
  type SourcingEvent,
  SOURCING_STATUS_LABELS,
} from '@/features/sourcing';
import { toast } from 'sonner';

interface StatusTab {
  value: SourcingStatus | undefined;
  label: string;
}

const STATUS_TABS: StatusTab[] = [
  {
    value: undefined,
    label: 'All' 
  },
  {
    value: 'open',
    label: 'Open' 
  },
  {
    value: 'scheduled',
    label: 'Scheduled' 
  },
  {
    value: 'evaluation',
    label: 'Evaluation' 
  },
  {
    value: 'awarded',
    label: 'Awarded' 
  },
];

export function SourcingView() {
  const [selectedStatus, setSelectedStatus] = useState<SourcingStatus | undefined>(undefined);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<SourcingEvent | null>(null);
  const [formData, setFormData] = useState<AddEventFormData>({
    title: '',
    eventType: 'sealed_proposal',
    deliveryZone: '',
    requiredDeliveryDate: '',
    bidDeadline: '',
    estimatedValue: '',
    description: '',
  });

  const {
    data, isLoading, error, refetch 
  } = useSourcingEvents();

  const allEvents = data?.data ?? [];
  const events = selectedStatus
    ? allEvents.filter((e) => e.status === selectedStatus)
    : allEvents;

  const stats = useMemo(() => {
    return {
      total: allEvents.length,
      open: allEvents.filter((e) => e.status === 'open').length,
      evaluation: allEvents.filter((e) => e.status === 'evaluation').length,
      totalValue: allEvents.reduce((sum, e) => sum + e.estimatedValue, 0),
    };
  }, [allEvents]);

  const handleAddEvent = () => {
    setFormData({
      title: '',
      eventType: 'sealed_proposal',
      deliveryZone: '',
      requiredDeliveryDate: '',
      bidDeadline: '',
      estimatedValue: '',
      description: '',
    });
    setShowAddModal(true);
  };

  const handleSaveEvent = () => {
    if (!formData.title || !formData.deliveryZone || !formData.bidDeadline) {
      toast.error('Please fill in all required fields');
      return;
    }
    toast.success('Sourcing event created');
    setShowAddModal(false);
    refetch();
  };

  const handleBulkUpload = (file: File) => {
    toast.success(`Processing ${file.name}...`);
    setTimeout(() => {
      toast.success('Imported 2 sourcing events');
      refetch();
    }, 1500);
  };

  const handleCardClick = (event: SourcingEvent) => {
    setSelectedEvent(event);
  };

  const handleStartAuction: (eventId: string) => void = () => {
    toast.success('Auction started');
    setSelectedEvent(null);
    refetch();
  };

  const handleEndAuction: (eventId: string) => void = () => {
    toast.success('Auction ended');
    setSelectedEvent(null);
    refetch();
  };

  const handleAwardBid: (eventId: string) => void = () => {
    toast.success('Bid awarded');
    setSelectedEvent(null);
    refetch();
  };

  const handleCancelEvent: (eventId: string) => void = () => {
    toast.success('Event cancelled');
    setSelectedEvent(null);
    refetch();
  };

  const exportData = events.map((event) => ({
    eventNumber: event.eventNumber,
    title: event.title,
    type: event.eventType,
    status: SOURCING_STATUS_LABELS[event.status],
    deliveryZone: event.deliveryZone,
    estimatedValue: event.estimatedValue,
    bidDeadline: event.bidDeadline,
    totalBids: event.totalBids ?? 0,
    createdBy: event.createdByName,
    createdAt: event.createdAt,
  }));

  return (
    <>
      <CrudToolbar
        entityName="Sourcing Event"
        entityNamePlural="Sourcing Events"
        selectedCount={0}
        onAdd={handleAddEvent}
        onBulkUpload={handleBulkUpload}
        exportData={exportData}
        exportFilename="sourcing-events.csv"
        canEdit={false}
        canDelete={false}
      />

      <AddEventModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleSaveEvent}
        formData={formData}
        setFormData={setFormData}
      />

      {selectedEvent && (
        <SourcingEventDetailModal
          event={selectedEvent}
          isOpen={true}
          onClose={() => setSelectedEvent(null)}
          onStartAuction={handleStartAuction}
          onEndAuction={handleEndAuction}
          onAwardBid={handleAwardBid}
          onCancel={handleCancelEvent}
        />
      )}

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Events
            </CardTitle>
            <ShoppingCart className="h-5 w-5 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Open for Bids
            </CardTitle>
            <Gavel className="h-5 w-5 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.open}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Under Evaluation
            </CardTitle>
            <Clock className="h-5 w-5 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">{stats.evaluation}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Value
            </CardTitle>
            <DollarSign className="h-5 w-5 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              ${stats.totalValue.toLocaleString()}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUS_TABS.map((tab) => (
          <Button
            key={tab.label}
            variant={selectedStatus === tab.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedStatus(tab.value)}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {isLoading && (
        <div className="grid gap-4 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={`skel-${i.toString()}`} className="h-64 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      )}

      {!isLoading && error && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertTriangle className="mb-4 h-12 w-12 text-red-500" />
            <p className="text-lg font-medium">Failed to load sourcing events</p>
            <Button variant="outline" className="mt-4" onClick={() => refetch()}>
              Try Again
            </Button>
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && events.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ShoppingCart className="mb-4 h-12 w-12 text-muted-foreground" />
            <p className="text-lg font-medium">Sourcing Events</p>
            <p className="text-sm text-muted-foreground">
              Broadcast requirements to supplier network
            </p>
            <Button className="mt-4" onClick={handleAddEvent}>
              Create Sourcing Event
            </Button>
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && events.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2">
          {events.map((event) => (
            <div
              key={event.eventId}
              className="cursor-pointer rounded-lg transition-all hover:ring-2 hover:ring-primary/50"
              onClick={() => handleCardClick(event)}
            >
              <SourcingEventCard event={event} />
            </div>
          ))}
        </div>
      )}
    </>
  );
}
